import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, Inject, Input, OnInit, Optional, Output, QueryList, ViewChildren } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AuthorizationService } from 'src/app/core/services/authorization/authorization.service';
import { ConfigService } from 'src/app/core/services/config/config.service';
import { ExternalCrewInfo } from 'src/app/models/ExternalCrewInfo';
import { ExternalCrewDataUpdate } from 'src/app/models/ExternalCrewDataUpdate';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { GoogleAuthenticationService } from 'src/app/core/services/login/google-authentication.service';
import { CREW_INFO, getItemFromSessionStorage } from 'src/app/utils/SessionStorageUtil';
import { CrewInfoStore } from 'src/app/core/services/crew-info/crew-info.store';

declare var grecaptcha: any;

type ChangePasswordDialogMode = 'first-access' | 'details' | 'mfa';

interface ChangePasswordDialogData {
  crew: ExternalCrewInfo;
  mode?: ChangePasswordDialogMode;
}

function passwordMatchValidator(field1: string, field2: string): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const value1 = group.get(field1)?.value;
    const value2 = group.get(field2)?.value;
    if (value1 && value2 && value1 !== value2) {
      return { passwordEquals: true };
    }
    return null;
  };
}

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss'],
  standalone: false
})
export class ChangePasswordComponent implements OnInit {

  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef<HTMLInputElement>>;

  // Inputs for standalone mode (when not used as dialog)
  @Input() crew?: ExternalCrewInfo;
  @Input() mode?: ChangePasswordDialogMode;
  @Input() standalone: boolean = false;

  // Output for standalone mode
  @Output() validationComplete = new EventEmitter<{ mfaVerified?: boolean }>();

  private readonly otpLength: number = 6;
  private readonly customEmailDomainCompanies: string[] = ['JJ', 'M3'];

  step: 1 | 2 | 3 = 1;
  submited: boolean = false;
  loading: boolean = false;
  resendLoading: boolean = false;
  emailPrefixDomainAlert: boolean = false;
  otpSlots: number[] = Array.from({ length: this.otpLength }, (_, index) => index);
  otpValues: string[] = Array(this.otpLength).fill('');

  emailForm: FormGroup = new FormGroup({
    emailPrefix: new FormControl('', [Validators.required, Validators.pattern('^[a-zA-Z0-9._+\\-]+$')]),
    emailDomain: new FormControl('')
  });

  codeForm: FormGroup = new FormGroup({
    verificationCode: new FormControl('', [Validators.required, Validators.minLength(6), Validators.maxLength(6), Validators.pattern('^[0-9]{6}$')])
  });

  passwordForm: FormGroup = new FormGroup({
    newPassword: new FormControl('', [Validators.required, Validators.minLength(6)]),
    confirmPassword: new FormControl('', Validators.required)
  });

  crewInfo!: ExternalCrewInfo;
  dialogMode: ChangePasswordDialogMode = 'first-access';
  protected emailFull: string = '';

  constructor(
    private authorizationService: AuthorizationService,
    private configService: ConfigService,
    private router: Router,
    private notificationService: NotificationService,
    private googleAuthenticationService: GoogleAuthenticationService,
    private crewInfoStore: CrewInfoStore,
    @Optional() protected dialogRef: MatDialogRef<ChangePasswordComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data?: ChangePasswordDialogData | ExternalCrewInfo
  ) { }

  ngOnInit(): void {
    // Initialize from inputs (standalone mode) or dialog data (dialog mode)
    if (this.standalone && this.crew && this.mode) {
      // Standalone mode: use @Input() values
      this.crewInfo = this.crew;
      this.dialogMode = this.mode;
    } else if (this.data) {
      // Dialog mode: use MAT_DIALOG_DATA
      if (this.isDialogData(this.data)) {
        this.crewInfo = this.data.crew;
        this.dialogMode = this.data.mode ?? 'first-access';
      } else {
        this.crewInfo = this.data;
      }
    }

    if (this.dialogMode === 'mfa') {
      this.step = 2;
      this.emailFull = this.crewInfo.maskedEmail;
      this.loading = false;
      this.focusOtpInput(0);
      this.notificationService.success(`Código enviado para ${this.emailFull}`);
    }

    this.initializeCompanyDomainSelection();
    this.configureEmailValidators();
    this.initializeEmailStepState();

    this.passwordForm.addValidators(
      passwordMatchValidator('newPassword', 'confirmPassword')
    );
  }

  get companyDomain(): string {
    if (this.allowsCustomEmailDomain) {
      return '';
    }

    const selectedDomain = this.emailForm.get('emailDomain')?.value?.trim().toLowerCase() ?? '';
    if (selectedDomain.startsWith('@')) {
      return selectedDomain;
    }

    return this.companyDomains[0] ?? '';
  }

  get companyDomains(): string[] {
    if (this.allowsCustomEmailDomain) {
      return [];
    }

    const domains = this.crewInfo?.companyDomains?.length
      ? this.crewInfo.companyDomains
      : (this.crewInfo?.companyDomain ? [this.crewInfo.companyDomain] : []);

    return domains
      .map(domain => domain?.trim().toLowerCase() ?? '')
      .filter((domain, index, normalizedDomains) =>
        domain.startsWith('@') && normalizedDomains.indexOf(domain) === index
      );
  }

  get hasMultipleCompanyDomains(): boolean {
    return this.companyDomains.length > 1;
  }

  get allowsCustomEmailDomain(): boolean {
    const company = this.crewInfo?.company?.trim().toUpperCase() ?? '';
    return this.customEmailDomainCompanies.includes(company);
  }

  get canResendCode(): boolean {
    return !this.loading && !this.resendLoading;
  }

  get canValidateEmail(): boolean {
    const prefix = this.emailForm.get('emailPrefix')?.value?.trim();
    return !this.loading && !!prefix && this.emailForm.valid;
  }

  get showFirstAccessFlow(): boolean {
    return this.dialogMode === 'first-access' || this.dialogMode === 'mfa';
  }

  get isDetailsOnlyView(): boolean {
    return this.dialogMode === 'details';
  }

  get hasRegisteredEmail(): boolean {
    return !!this.crewInfo?.email?.trim();
  }

  /** Habilita "Aceitar" solo cuando las 6 casillas tienen dígitos y el código es válido. */
  get canAcceptVerificationCode(): boolean {
    if (this.loading) return false;
    return this.codeForm.get('verificationCode')?.valid === true;
  }

  onEmailPrefixKeyDown(event: KeyboardEvent): void {
    if (this.allowsCustomEmailDomain) {
      if (event.key === ' ') {
        event.preventDefault();
      }
      return;
    }

    if (event.key === '@' || event.key === ' ') {
      event.preventDefault();
      this.emailPrefixDomainAlert = event.key === '@';
    }
  }

  onEmailPrefixInput(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (this.allowsCustomEmailDomain) {
      const sanitizedValue = this.sanitizeFullEmail(input.value);

      if (input.value !== sanitizedValue) {
        input.value = sanitizedValue;
      }

      this.emailPrefixDomainAlert = false;
      this.emailForm.get('emailPrefix')?.setValue(sanitizedValue, { emitEvent: false });
      return;
    }

    const hadDomainAttempt = input.value.includes('@');
    const sanitizedValue = this.sanitizeEmailPrefix(input.value);

    if (input.value !== sanitizedValue) {
      input.value = sanitizedValue;
    }

    this.emailPrefixDomainAlert = hadDomainAttempt ? true : false;
    this.emailForm.get('emailPrefix')?.setValue(sanitizedValue);
  }

  onOtpInput(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '');

    if (!digits) {
      this.otpValues[index] = '';
      input.value = '';
      this.updateVerificationCodeControl();
      return;
    }

    if (digits.length > 1) {
      this.fillOtpValues(digits, index);
      return;
    }

    this.otpValues[index] = digits;
    input.value = digits;
    this.updateVerificationCodeControl();

    if (index < this.otpLength - 1) {
      this.focusOtpInput(index + 1);
    }
  }

  onOtpKeyDown(index: number, event: KeyboardEvent): void {
    if (event.key === 'Backspace') {
      event.preventDefault();

      if (this.otpValues[index]) {
        this.otpValues[index] = '';
        this.syncOtpInputs();
        this.updateVerificationCodeControl();
        return;
      }

      if (index > 0) {
        this.otpValues[index - 1] = '';
        this.syncOtpInputs();
        this.updateVerificationCodeControl();
        this.focusOtpInput(index - 1);
      }

      return;
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      this.focusOtpInput(index - 1);
    }

    if (event.key === 'ArrowRight' && index < this.otpLength - 1) {
      event.preventDefault();
      this.focusOtpInput(index + 1);
    }
  }

  onOtpPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') ?? '';
    this.fillOtpValues(pastedText, 0);
  }

  onOtpFocus(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    input.select();
  }

  private hasValidToken(): boolean {
    const token = this.googleAuthenticationService.getAccessToken();
    return !!token && token !== 'undefined' && token !== 'null' && token.trim() !== '';
  }

  validateEmail(): void {
    this.submited = true;
    if (this.emailForm.invalid) return;

    if (!this.hasValidToken()) {
      this.notificationService.error('Sessão inválida. Por favor, faça login novamente.');
      this.closeComponent();
      this.router.navigate(['/external-login']);
      return;
    }

    const emailValue = this.emailForm.get('emailPrefix')!.value.trim();
    this.emailFull = this.allowsCustomEmailDomain ? emailValue : `${emailValue}${this.companyDomain}`;
    this.loading = true;

    const crewInfo = getItemFromSessionStorage<ExternalCrewInfo>(CREW_INFO);
    if (!crewInfo) {
      this.loading = false;
      return;
    }

    this.authorizationService.sendEmailCode(crewInfo.code.value, this.emailFull).subscribe({
      next: () => {
        this.submited = false;
        this.loading = false;
        this.emailPrefixDomainAlert = false;
        this.step = 2;
        this.resetOtpState();
        this.focusOtpInput(0);
        this.notificationService.success(`Código enviado para ${this.emailFull}`);
      },
      error: (error: HttpErrorResponse) => {
        this.loading = false;
        this.notificationService.error('Erro ao enviar o código. Tente novamente.');
      }
    });
  }

  validateCode(): void {
    this.submited = true;
    if (this.codeForm.invalid) return;

    if (!this.hasValidToken()) {
      this.notificationService.error('Sessão inválida. Por favor, faça login novamente.');
      this.closeComponent();
      this.router.navigate(['/external-login']);
      return;
    }

    const code = this.codeForm.get('verificationCode')!.value.trim();
    this.loading = true;

    const crewInfo = getItemFromSessionStorage<ExternalCrewInfo>(CREW_INFO);
    if (!crewInfo) {
      this.loading = false;
      return;
    }

    this.authorizationService.validateEmailCode(crewInfo.code.value, code).subscribe({
      next: (response: any) => {
        this.submited = false;
        this.loading = false;
        if (this.dialogMode === 'mfa') {
          crewInfo.requiresMfa = false;
          this.crewInfoStore.setCrewInfo(crewInfo);
          if (response && response.token) {
            this.googleAuthenticationService.setAccessToken(response.token);
          }
          this.closeComponent({ mfaVerified: true });
        } else {
          this.step = 3;
        }
      },
      error: (error: HttpErrorResponse) => {
        this.loading = false;
        if (error.status === 403) {
          this.notificationService.error('Código inválido ou expirado. Tente novamente.');
        } else {
          this.notificationService.error('Ocorreu um erro inesperado.');
        }
      }
    });
  }

  resendCode(): void {
    if (!this.canResendCode || !this.emailFull) return;

    if (!this.hasValidToken()) {
      this.notificationService.error('Sessão inválida. Por favor, faça login novamente.');
      this.closeComponent();
      this.router.navigate(['/external-login']);
      return;
    }

    const crewInfo = getItemFromSessionStorage<ExternalCrewInfo>(CREW_INFO);
    if (!crewInfo) return;

    this.resendLoading = true;

    this.authorizationService.sendEmailCode(crewInfo.code.value, this.emailFull).subscribe({
      next: () => {
        this.resendLoading = false;
        this.resetOtpState();
        this.focusOtpInput(0);
        this.notificationService.success(`Novo código enviado para ${this.emailFull}`);
      },
      error: () => {
        this.resendLoading = false;
        this.notificationService.error('Erro ao reenviar o código. Tente novamente.');
      }
    });
  }

  editEmail(): void {
    if (this.loading || this.resendLoading) return;

    this.step = 1;
    this.resetOtpState();
  }

  async confirm(): Promise<void> {
    this.submited = true;
    if (this.passwordForm.invalid) return;

    const crewInfo = getItemFromSessionStorage<ExternalCrewInfo>(CREW_INFO);
    if (!crewInfo) return;

    const data = this.passwordForm.getRawValue();
    const externalCrewDataUpdate = new ExternalCrewDataUpdate();
    externalCrewDataUpdate.anacCode = crewInfo.code.value;
    externalCrewDataUpdate.email = this.emailFull;
    externalCrewDataUpdate.password = data.newPassword;

    this.loading = true;
    const tokenReCaptcha = await this.genereteReCaptchaToken();

    this.authorizationService.externalCrewUpdate(externalCrewDataUpdate, tokenReCaptcha).subscribe({
      next: () => {
        this.loading = false;
        this.closeComponent();
        this.router.navigate(['/external-login']);
        this.notificationService.success('Senha definida com sucesso! Faça login novamente.');
      },
      error: () => {
        this.loading = false;
        this.notificationService.error('Ocorreu um erro inesperado!');
      }
    });
  }

  private async genereteReCaptchaToken(): Promise<string> {
    const token = await grecaptcha.enterprise.execute(this.configService.settings.recaptcha_key, { action: 'externalLogin' });
    return token;
  }

  private fillOtpValues(rawValue: string, startIndex: number): void {
    const digits = rawValue.replace(/\D/g, '').slice(0, this.otpLength - startIndex).split('');

    if (!digits.length) {
      return;
    }

    digits.forEach((digit, offset) => {
      this.otpValues[startIndex + offset] = digit;
    });

    this.syncOtpInputs();
    this.updateVerificationCodeControl();

    const nextIndex = Math.min(startIndex + digits.length, this.otpLength - 1);
    this.focusOtpInput(nextIndex);
  }

  private updateVerificationCodeControl(): void {
    this.codeForm.get('verificationCode')?.setValue(this.otpValues.join(''), { emitEvent: false });
  }

  private resetOtpState(): void {
    this.otpValues = Array(this.otpLength).fill('');
    this.syncOtpInputs();
    this.codeForm.get('verificationCode')?.reset('', { emitEvent: false });
    this.submited = false;
  }

  private syncOtpInputs(): void {
    if (!this.otpInputs) return;

    this.otpInputs.forEach((inputRef, index) => {
      inputRef.nativeElement.value = this.otpValues[index] ?? '';
    });
  }

  private focusOtpInput(index: number): void {
    setTimeout(() => {
      const input = this.otpInputs?.get(index)?.nativeElement;
      if (!input) return;

      input.focus();
      input.select();
    });
  }

  private sanitizeEmailPrefix(value: string): string {
    const prefixOnly = value.split('@')[0] ?? '';
    return prefixOnly
      .replace(/\s+/g, '')
      .toLowerCase()
      .replace(/[^a-zA-Z0-9._+\-]/g, '');
  }

  private sanitizeFullEmail(value: string): string {
    return value.replace(/\s+/g, '').toLowerCase();
  }

  private configureEmailValidators(): void {
    const emailControl = this.emailForm.get('emailPrefix');
    const domainControl = this.emailForm.get('emailDomain');
    if (!emailControl) {
      return;
    }

    if (this.allowsCustomEmailDomain) {
      emailControl.setValidators([Validators.required, Validators.email]);
    } else {
      emailControl.setValidators([Validators.required, Validators.pattern('^[a-zA-Z0-9._+\\-]+$')]);
    }

    emailControl.updateValueAndValidity({ emitEvent: false });

    if (this.hasMultipleCompanyDomains) {
      domainControl?.setValidators([Validators.required]);
    } else {
      domainControl?.clearValidators();
    }

    domainControl?.updateValueAndValidity({ emitEvent: false });
  }

  private initializeCompanyDomainSelection(): void {
    const domainControl = this.emailForm.get('emailDomain');
    if (!domainControl || this.allowsCustomEmailDomain) {
      domainControl?.setValue('', { emitEvent: false });
      return;
    }

    const domains = this.companyDomains;
    const registeredEmail = this.crewInfo?.email?.trim().toLowerCase() ?? '';
    const matchingDomain = domains.find(domain => registeredEmail.endsWith(domain));

    domainControl.setValue(matchingDomain ?? domains[0] ?? '', { emitEvent: false });
  }

  private initializeEmailStepState(): void {
    if (!this.showFirstAccessFlow || !this.hasRegisteredEmail) {
      return;
    }

    const emailControl = this.emailForm.get('emailPrefix');
    if (!emailControl) {
      return;
    }

    const registeredEmail = this.crewInfo.email.trim().toLowerCase();
    const initialValue = this.resolveInitialEmailValue(registeredEmail);

    emailControl.setValue(initialValue, { emitEvent: false });
    this.emailPrefixDomainAlert = false;
  }

  private resolveInitialEmailValue(email: string): string {
    if (this.allowsCustomEmailDomain || !this.companyDomain) {
      return this.sanitizeFullEmail(email);
    }

    if (email.endsWith(this.companyDomain)) {
      return this.sanitizeEmailPrefix(email.slice(0, -this.companyDomain.length));
    }

    return this.sanitizeEmailPrefix(email);
  }

  private isDialogData(data: ChangePasswordDialogData | ExternalCrewInfo): data is ChangePasswordDialogData {
    return 'crew' in data;
  }

  private closeComponent(result?: { mfaVerified?: boolean }): void {
    if (this.standalone) {
      // Standalone mode: emit event and navigate
      this.validationComplete.emit(result);
    } else if (this.dialogRef) {
      // Dialog mode: close dialog
      this.closeComponent(result);
    }
  }
}
